'use client'

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '@clerk/clerk-react'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Skeleton } from '@/components/ui/skeleton'
import { InfoIcon, Copy, Check, AlertCircle } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { config } from '@/lib/config'

// ============================================================================
// Types & Schema
// ============================================================================

const autoConnectSchema = z.object({
  email: z.string().email('Valid email is required'),
  password: z.string().min(1, 'Password is required'),
  integrationName: z.string().min(1, 'Integration name is required'),
  domain: z.string().url('Valid domain URL is required'),
})

const manualSetupSchema = z.object({
  apiKey: z.string().min(1, 'API Key is required'),
  clientKey: z.string().min(1, 'Client Key is required'),
  secretKey: z.string().min(1, 'Secret Key is required'),
  webhookSecret: z.string().min(1, 'Webhook Secret is required'),
  returnUrlOverride: z
    .string()
    .url('Must be a valid URL')
    .optional()
    .or(z.literal('')),
})

type AutoConnectValues = z.infer<typeof autoConnectSchema>
type ManualSetupValues = z.infer<typeof manualSetupSchema>

interface ConvesioPayFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
  storeId?: string
  accountId?: string
}

interface CustomDomain {
  id: string
  domain: string
  isVerified: boolean
}

// ============================================================================
// Component
// ============================================================================

export function ConvesioPayForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
  storeId,
  accountId,
}: ConvesioPayFormProps) {
  const { toast } = useToast()
  const { t } = useTranslation('integrations')
  const { getToken } = useAuth()

  // Determine if already connected
  const hasExistingCredentials = !!(defaultValues.credentials?.apiKey)
  const connectedIntegrationName = defaultValues.credentials?.connectedIntegrationName
  const isAutoConnected = hasExistingCredentials && !!connectedIntegrationName

  // State
  const [mode, setMode] = useState<'auto' | 'manual'>(hasExistingCredentials ? 'manual' : 'auto')
  const [testMode, setTestMode] = useState(defaultValues.credentials?.testMode ?? false)
  const [storePaymentMethod, setStorePaymentMethod] = useState(
    defaultValues.credentials?.storePaymentMethod ?? true
  )
  const [isConnecting, setIsConnecting] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [copiedWebhook, setCopiedWebhook] = useState(false)

  // Domain selection state
  const [customDomains, setCustomDomains] = useState<CustomDomain[]>([])
  const [loadingDomains, setLoadingDomains] = useState(false)
  const [selectedDomainType, setSelectedDomainType] = useState<'predefined' | 'custom'>('predefined')
  const [selectedDomainId, setSelectedDomainId] = useState<string>('')
  const [customDomainInput, setCustomDomainInput] = useState('')

  // Auto Connect Form
  const autoConnectForm = useForm<AutoConnectValues>({
    resolver: zodResolver(autoConnectSchema),
    defaultValues: {
      email: '',
      password: '',
      integrationName: 'Tagada',
      domain: config.apiBaseUrl || '',
    },
  })

  // Manual Setup Form
  const manualSetupForm = useForm<ManualSetupValues>({
    resolver: zodResolver(manualSetupSchema),
    defaultValues: {
      apiKey: defaultValues.credentials?.apiKey ?? '',
      clientKey: defaultValues.credentials?.clientKey ?? '',
      secretKey: defaultValues.credentials?.secretKey ?? '',
      webhookSecret: defaultValues.credentials?.webhookSecret ?? '',
      returnUrlOverride: defaultValues.credentials?.returnUrlOverride ?? '',
    },
  })

  // Webhook URL
  const webhookUrl = `${config.apiBaseUrl}/api/public/webhooks/convesiopay`

  // Fetch custom domains
  useEffect(() => {
    const fetchDomains = async () => {
      setLoadingDomains(true)
      try {
        const token = await getToken()
        const response = await fetch(`${config.apiBaseUrl}/api/dashboard/custom-domains/list`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        })

        if (response.ok) {
          const data = await response.json()
          if (data[0]?.domains) {
            setCustomDomains(data[0].domains)
          }
        }
      } catch (error) {
        console.error('Error fetching custom domains:', error)
      } finally {
        setLoadingDomains(false)
      }
    }

    if (mode === 'auto') {
      fetchDomains()
    }
  }, [mode, getToken])

  // Update domain in form when selection changes
  useEffect(() => {
    if (selectedDomainType === 'predefined' && selectedDomainId) {
      const domain = customDomains.find(d => d.id === selectedDomainId)
      if (domain) {
        autoConnectForm.setValue('domain', `https://${domain.domain}`)
      }
    } else if (selectedDomainType === 'custom' && customDomainInput) {
      autoConnectForm.setValue('domain', customDomainInput)
    }
  }, [selectedDomainType, selectedDomainId, customDomainInput, customDomains, autoConnectForm])

  // Handle auto-connect
  const handleAutoConnect = async (data: AutoConnectValues) => {
    setIsConnecting(true)
    try {
      const token = await getToken()
      const response = await fetch(`${config.apiBaseUrl}/api/dashboard/convesiopay/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          integrationName: data.integrationName,
          domain: data.domain,
          testMode,
          storePaymentMethod,
        }),
      })

      if (!response.ok) {
        const error = await response.text()
        throw new Error(error || t('convesiopay.connectionFailedDescription'))
      }

      const result = await response.json()
      const credentials = Array.isArray(result) ? result[0] : result

      // Populate manual form with generated credentials
      manualSetupForm.setValue('apiKey', credentials.apiKey)
      manualSetupForm.setValue('clientKey', credentials.clientKey)
      manualSetupForm.setValue('secretKey', credentials.secretKey)
      manualSetupForm.setValue('webhookSecret', credentials.webhookSecret)

      toast({
        title: t('convesiopay.connectedSuccess'),
        description: t('convesiopay.connectedSuccessDescription'),
      })

      // Switch to manual mode to show credentials
      setMode('manual')

      // Auto-save the integration
      await onSubmit({
        credentials: {
          apiKey: credentials.apiKey,
          clientKey: credentials.clientKey,
          secretKey: credentials.secretKey,
          webhookSecret: credentials.webhookSecret,
          testMode,
          storePaymentMethod,
          connectedIntegrationName: credentials.connectedIntegrationName,
        },
      })
    } catch (error) {
      toast({
        title: t('convesiopay.connectionFailed'),
        description: error instanceof Error ? error.message : t('convesiopay.connectionFailedDescription'),
        variant: 'destructive',
      })
    } finally {
      setIsConnecting(false)
    }
  }

  // Handle disconnect
  const handleDisconnect = async () => {
    if (!storeId) {
      toast({
        title: t('toasts.errorTitle'),
        description: t('convesiopay.errorStoreRequired'),
        variant: 'destructive',
      })
      return
    }

    if (!confirm(t('convesiopay.disconnectConfirm'))) {
      return
    }

    setIsDisconnecting(true)
    try {
      const token = await getToken()
      const response = await fetch(`${config.apiBaseUrl}/api/dashboard/convesiopay/disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ storeId }),
      })

      if (!response.ok) {
        const error = await response.text()
        throw new Error(error || t('convesiopay.disconnectionFailedDescription'))
      }

      // Clear all form values
      manualSetupForm.reset({
        apiKey: '',
        clientKey: '',
        secretKey: '',
        webhookSecret: '',
        returnUrlOverride: '',
      })

      toast({
        title: t('convesiopay.disconnectedSuccess'),
        description: t('convesiopay.disconnectedSuccessDescription'),
      })

      // Submit the cleared integration
      await onSubmit({
        credentials: null,
      })
    } catch (error) {
      toast({
        title: t('convesiopay.disconnectionFailed'),
        description: error instanceof Error ? error.message : t('convesiopay.disconnectionFailedDescription'),
        variant: 'destructive',
      })
    } finally {
      setIsDisconnecting(false)
    }
  }

  // Handle manual form submit
  const handleManualSubmit = async (data: ManualSetupValues) => {
    const { returnUrlOverride, ...rest } = data
    await onSubmit({
      credentials: {
        ...rest,
        testMode,
        storePaymentMethod,
        ...(returnUrlOverride ? { returnUrlOverride } : {}),
        ...(connectedIntegrationName && { connectedIntegrationName }),
      },
    })
  }

  // Copy webhook URL
  const handleCopyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl)
      setCopiedWebhook(true)
      setTimeout(() => setCopiedWebhook(false), 2000)
      toast({
        title: t('convesiopay.copied'),
        description: t('toasts.webhookUrlCopied'),
      })
    } catch (error) {
      toast({
        title: t('toasts.errorTitle'),
        description: t('toasts.failedToCopyWebhook'),
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="space-y-6">{/* Connection Status Alert */}
      {hasExistingCredentials && (
        <Alert className="border-green-200 bg-green-50">
          <Check className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-900">{t('convesiopay.integrationConnected')}</AlertTitle>
          <AlertDescription className="text-green-700">
            {isAutoConnected ? t('convesiopay.connectedViaAuto') : t('convesiopay.connectedManualView')}
          </AlertDescription>
        </Alert>
      )}

      {/* Mode Tabs */}
      <Tabs value={mode} onValueChange={(v) => setMode(v as 'auto' | 'manual')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="auto">{t('convesiopay.autoConnect')}</TabsTrigger>
          <TabsTrigger value="manual">{t('convesiopay.manualSetup')}</TabsTrigger>
        </TabsList>

        {/* Auto Connect Tab */}
        <TabsContent value="auto" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('convesiopay.autoConnectTitle')}</CardTitle>
              <CardDescription>
                {t('convesiopay.autoConnectDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={autoConnectForm.handleSubmit(handleAutoConnect)} className="space-y-4">
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email">
                    {t('convesiopay.accountEmail')}
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('convesiopay.emailPlaceholder')}
                    disabled={isConnecting || isLoading}
                    {...autoConnectForm.register('email')}
                  />
                  {autoConnectForm.formState.errors.email && (
                    <p className="text-xs text-destructive">
                      {autoConnectForm.formState.errors.email.message}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="password">
                    {t('convesiopay.accountPassword')}
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder={t('convesiopay.passwordPlaceholder')}
                    disabled={isConnecting || isLoading}
                    {...autoConnectForm.register('password')}
                  />
                  {autoConnectForm.formState.errors.password && (
                    <p className="text-xs text-destructive">
                      {autoConnectForm.formState.errors.password.message}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {t('convesiopay.passwordNotStored')}
                  </p>
                </div>

                {/* Integration Name */}
                <div className="space-y-2">
                  <Label htmlFor="integrationName">
                    {t('convesiopay.integrationName')}
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <Input
                    id="integrationName"
                    placeholder={t('convesiopay.integrationNamePlaceholder')}
                    disabled={isConnecting || isLoading}
                    {...autoConnectForm.register('integrationName')}
                  />
                  {autoConnectForm.formState.errors.integrationName && (
                    <p className="text-xs text-destructive">
                      {autoConnectForm.formState.errors.integrationName.message}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {t('convesiopay.integrationNameHint')}
                  </p>
                </div>

                {/* Domain Selection */}
                <div className="space-y-2">
                  <Label>
                    {t('convesiopay.selectDomain')}
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    {t('convesiopay.selectDomainHint')}
                  </p>

                  {loadingDomains ? (
                    <Card className="p-4">
                      <Skeleton className="h-10 w-full mb-2" />
                      <Skeleton className="h-10 w-full" />
                    </Card>
                  ) : customDomains.length > 0 ? (
                    <RadioGroup
                      value={selectedDomainType === 'predefined' ? selectedDomainId : 'custom'}
                      onValueChange={(value) => {
                        if (value === 'custom') {
                          setSelectedDomainType('custom')
                          setSelectedDomainId('')
                        } else {
                          setSelectedDomainType('predefined')
                          setSelectedDomainId(value)
                        }
                      }}
                    >
                      <Card className="p-4 space-y-3">
                        {customDomains.map((domain) => (
                          <div key={domain.id} className="flex items-center space-x-2">
                            <RadioGroupItem value={domain.id} id={`domain-${domain.id}`} />
                            <Label htmlFor={`domain-${domain.id}`} className="cursor-pointer flex-1">
                              {domain.domain}
                              {domain.isVerified && (
                                <span className="ml-2 text-xs text-green-600">({t('convesiopay.verified')})</span>
                              )}
                            </Label>
                          </div>
                        ))}
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="custom" id="domain-custom" />
                          <Label htmlFor="domain-custom" className="cursor-pointer">
                            {t('convesiopay.useCustomDomain')}
                          </Label>
                        </div>
                      </Card>
                    </RadioGroup>
                  ) : (
                    <Alert className="border-amber-200 bg-amber-50">
                      <InfoIcon className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-700">
                        {t('convesiopay.noDomainsFound')}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Custom Domain Input */}
                  {(selectedDomainType === 'custom' || customDomains.length === 0) && (
                    <div className="mt-2 space-y-2">
                      <Label htmlFor="customDomain">{t('convesiopay.customDomain')}</Label>
                      <Input
                        id="customDomain"
                        placeholder={t('convesiopay.customDomainPlaceholder')}
                        value={customDomainInput}
                        onChange={(e) => setCustomDomainInput(e.target.value)}
                        disabled={isConnecting || isLoading}
                      />
                      <p className="text-xs text-muted-foreground">
                        {t('convesiopay.customDomainHint')}
                      </p>
                    </div>
                  )}

                  {/* Selected Domain Display */}
                  {selectedDomainType === 'predefined' && selectedDomainId && (
                    <Alert className="mt-2 border-green-200 bg-green-50">
                      <InfoIcon className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-700">
                        {t('convesiopay.selected')}: <code className="bg-green-100 px-1 rounded">
                          https://{customDomains.find(d => d.id === selectedDomainId)?.domain}
                        </code>
                      </AlertDescription>
                    </Alert>
                  )}

                  {autoConnectForm.formState.errors.domain && (
                    <p className="text-xs text-destructive">
                      {autoConnectForm.formState.errors.domain.message}
                    </p>
                  )}
                </div>

                {/* Test Mode */}
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="testMode-auto" className="font-medium">{t('convesiopay.testMode')}</Label>
                    <p className="text-xs text-muted-foreground">
                      {t('convesiopay.testModeHint')}
                    </p>
                  </div>
                  <Switch
                    id="testMode-auto"
                    checked={testMode}
                    onCheckedChange={setTestMode}
                    disabled={isConnecting || isLoading}
                  />
                </div>

                {/* Store Payment Method */}
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="storePaymentMethod-auto" className="font-medium">
                      {t('convesiopay.storePaymentMethods')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t('convesiopay.storePaymentMethodsHint')}
                    </p>
                  </div>
                  <Switch
                    id="storePaymentMethod-auto"
                    checked={storePaymentMethod}
                    onCheckedChange={setStorePaymentMethod}
                    disabled={isConnecting || isLoading}
                  />
                </div>

                {/* Connect Button */}
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isConnecting || isLoading}
                >
                  {isConnecting ? t('convesiopay.connecting') : t('convesiopay.connectButton')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Manual Setup Tab */}
        <TabsContent value="manual" className="space-y-4">
          {/* Disconnect Button */}
          {isAutoConnected && (
            <Card className="border-destructive/50 bg-destructive/5">
              <CardHeader>
                <CardTitle className="text-destructive">{t('convesiopay.disconnectTitle')}</CardTitle>
                <CardDescription className="text-destructive/80">
                  {t('convesiopay.disconnectDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDisconnect}
                  disabled={isDisconnecting || isLoading}
                  className="w-full"
                >
                  {isDisconnecting ? t('convesiopay.disconnecting') : t('convesiopay.disconnectButton')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Credentials Form */}
          <form id="integration-config-form" onSubmit={manualSetupForm.handleSubmit(handleManualSubmit)} className="space-y-4">
            <Card>
              <CardHeader>
<CardTitle>{t('convesiopay.apiCredentials')}</CardTitle>
              <CardDescription>
                {t('convesiopay.apiCredentialsDescription')}
              </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* API Key */}
                <div className="space-y-2">
                  <Label htmlFor="apiKey">
                    {t('convesiopay.apiKey')}
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <Input
                    id="apiKey"
                    placeholder={t('convesiopay.apiKeyPlaceholder')}
                    disabled={isLoading}
                    {...manualSetupForm.register('apiKey')}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('convesiopay.apiKeyHint')}
                  </p>
                  {manualSetupForm.formState.errors.apiKey && (
                    <p className="text-xs text-destructive">
                      {manualSetupForm.formState.errors.apiKey.message}
                    </p>
                  )}
                </div>

                {/* Client Key */}
                <div className="space-y-2">
                  <Label htmlFor="clientKey">
                    {t('convesiopay.clientKey')}
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <Input
                    id="clientKey"
                    placeholder={t('convesiopay.clientKeyPlaceholder')}
                    disabled={isLoading}
                    {...manualSetupForm.register('clientKey')}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('convesiopay.clientKeyHint')}
                  </p>
                  {manualSetupForm.formState.errors.clientKey && (
                    <p className="text-xs text-destructive">
                      {manualSetupForm.formState.errors.clientKey.message}
                    </p>
                  )}
                </div>

                {/* Secret Key */}
                <div className="space-y-2">
                  <Label htmlFor="secretKey">
                    {t('convesiopay.secretKey')}
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <Input
                    id="secretKey"
                    type="password"
                    placeholder={t('convesiopay.secretKeyPlaceholder')}
                    disabled={isLoading}
                    {...manualSetupForm.register('secretKey')}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('convesiopay.secretKeyHint')}
                  </p>
                  {manualSetupForm.formState.errors.secretKey && (
                    <p className="text-xs text-destructive">
                      {manualSetupForm.formState.errors.secretKey.message}
                    </p>
                  )}
                </div>

                {/* Webhook Secret */}
                <div className="space-y-2">
                  <Label htmlFor="webhookSecret">
                    {t('convesiopay.webhookSecret')}
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <Input
                    id="webhookSecret"
                    type="password"
                    placeholder={t('convesiopay.webhookSecretPlaceholder')}
                    disabled={isLoading}
                    {...manualSetupForm.register('webhookSecret')}
                  />
                  {manualSetupForm.formState.errors.webhookSecret && (
                    <p className="text-xs text-destructive">
                      {manualSetupForm.formState.errors.webhookSecret.message}
                    </p>
                  )}

                  {/* Webhook URL Display */}
                  <div className="mt-2 space-y-2">
                    <Label className="text-xs font-medium">{t('convesiopay.webhookUrl')}:</Label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 rounded bg-muted px-3 py-2 text-xs">
                        {webhookUrl}
                      </code>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleCopyWebhook}
                      >
                        {copiedWebhook ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Connected Integration Name Display */}
                {connectedIntegrationName && (
                  <Alert className="border-green-200 bg-green-50">
                    <InfoIcon className="h-4 w-4 text-green-600" />
                    <AlertTitle className="text-green-900">{t('convesiopay.connectedIntegrationName')}</AlertTitle>
                    <AlertDescription className="text-green-700">
                      <code className="bg-green-100 px-2 py-1 rounded">
                        {connectedIntegrationName}
                      </code>
                      <p className="mt-1 text-xs">
                        {t('convesiopay.connectedIntegrationNameHint')}
                      </p>
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Settings */}
            <Card>
              <CardHeader>
                <CardTitle>{t('convesiopay.settings')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Test Mode */}
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="testMode" className="font-medium">{t('convesiopay.testMode')}</Label>
                    <p className="text-xs text-muted-foreground">
                      {t('convesiopay.testModeHint')}
                    </p>
                  </div>
                  <Switch
                    id="testMode"
                    checked={testMode}
                    onCheckedChange={setTestMode}
                    disabled={isLoading}
                  />
                </div>

                {/* Return URL Override */}
                <div className="space-y-2">
                  <Label htmlFor="returnUrlOverride" className="font-medium">
                    {t('convesiopay.returnUrlOverride')}
                  </Label>
                  <Input
                    id="returnUrlOverride"
                    placeholder={t('convesiopay.returnUrlOverridePlaceholder')}
                    disabled={isLoading}
                    {...manualSetupForm.register('returnUrlOverride')}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('convesiopay.returnUrlOverrideHint')}
                  </p>
                  {manualSetupForm.formState.errors.returnUrlOverride && (
                    <p className="text-xs text-destructive">
                      {manualSetupForm.formState.errors.returnUrlOverride.message}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Setup Instructions */}
            <Card>
              <CardHeader>
                <CardTitle>{t('convesiopay.manualSetupInstructions')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
                  <li>{t('convesiopay.manualSetupStep1')}</li>
                  <li dangerouslySetInnerHTML={{ __html: t('convesiopay.manualSetupStep2') }}></li>
                  <li>{t('convesiopay.manualSetupStep3')}</li>
                  <li>{t('convesiopay.manualSetupStep4')}: <code className="bg-muted px-1 rounded text-xs">{webhookUrl}</code></li>
                  <li>{t('convesiopay.manualSetupStep5')}</li>
                </ol>
              </CardContent>
            </Card>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  )
}
